/**
 * The invoice document model — ONE fully-resolved shape for both AR invoices
 * and AP bills — the shape the shared `InvoiceDocument` template draws (org
 * header block, bill-to/bill-from party, sorted line items, the totals
 * breakdown, payment history), with every value already derived. Templates
 * read it and format; they never fetch, sort, sum or fall back.
 *
 * Zod-first because this model crosses a wire: the `/model` endpoints return
 * it as JSON and the parity tests parse it.
 */
import { z } from "zod";
import { documentMetaSchema, orgIdentityBlockSchema } from "../common.js";
/** AR invoice (money in) or AP bill (money out) — one template, two sources. */
export const invoiceTypeSchema = z.enum(["ar", "ap"]);
/** `projects` embed as the invoice reads select it. */
export const invoiceProjectRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    code: z.string().nullable(),
});
/**
 * The counterparty's `business_vendor_identity` row, narrowed to exactly the
 * fields `VendorIdentityDisplay` prints (plus `countryCode`, which selects the
 * jurisdiction labels for the registration/VAT numbers). Fields the component
 * never shows (`legal_form_id`, `tax_entity_number`, timestamps) are
 * deliberately absent — same omit-by-default rule as `OrgBusinessIdentity`.
 */
export const invoiceVendorIdentitySchema = z.object({
    tradingName: z.string().nullable(),
    registrationNumber: z.string().nullable(),
    vatNumber: z.string().nullable(),
    countryCode: z.string().nullable(),
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    isRelatedParty: z.boolean(),
});
/**
 * Who the document is addressed to (AR: "Bill to" the customer) or from
 * (AP: "Bill from" the vendor). The name resolution differs per type and is
 * done in `buildInvoiceDocument` — see `resolveCounterpartyName`.
 */
export const invoiceCounterpartySchema = z.object({
    name: z.string(),
    email: z.string().nullable(),
    /**
     * Resolved from the counterparty's customer/vendor record (their primary
     * linked location, else `businesses.address`), with AR's legacy
     * `client_address` snapshot taking precedence when a row carries one — see
     * `fetchCounterpartyAddress` in assemble.ts.
     */
    address: z.string().nullable(),
    project: invoiceProjectRefSchema.nullable(),
    /** AP only today — see `buildInvoiceDocument`'s note. */
    vendorIdentity: invoiceVendorIdentitySchema.nullable(),
});
export const invoiceLineItemSchema = z.object({
    id: z.string(),
    description: z.string(),
    quantity: z.number(),
    unitPrice: z.number(),
    /** Percentage 0-100 (21 = 21%), matching `invoice_line_items.tax_rate`. */
    taxRate: z.number(),
    /** The line's `tax_rate_id` (both types select it, see selects.ts); null when none. */
    taxRateId: z.string().nullable(),
    /** 'standard' | 'exempt' | 'reverse_charge' | 'zero_rated' | 'non_reclaimable' (web's `TaxTreatment`; the last on AP bills only). */
    taxTreatment: z.string(),
    taxInclusive: z.boolean(),
    taxAmount: z.number(),
    /** Net of tax — what the preview shows in its "Subtotal" column. */
    lineTotal: z.number(),
    lineTotalWithTax: z.number(),
    exemptionReason: z.string().nullable(),
    exemptionReasonOther: z.string().nullable(),
    /** AR only: `service_categories.category_name`. AP lines have no category. */
    serviceCategory: z.object({ name: z.string() }).nullable(),
    /** Kept for provenance — `lineItems` is already sorted by it. */
    sortOrder: z.number(),
});
/**
 * One row of the tax breakdown: all tax on lines charged at `rate`, summed.
 * `name` is the tax rate's own name ("BTW 21%") when the lines' tax rates (or,
 * for a line with none, the posting journal) identify it unambiguously, else
 * null — the template falls back to "<taxName> <rate>%", which is what the
 * screen prints today.
 */
export const invoiceTaxByRateSchema = z.object({
    rate: z.number(),
    name: z.string().nullable(),
    amount: z.number(),
});
/**
 * The money block. Derived exactly as `InvoiceTotalsBreakdown` derives it:
 * `subtotal` is the sum of the lines' net totals (NOT `amountTotal` minus tax),
 * and `taxByRate` groups only lines with both a positive rate and a positive
 * tax amount, sorted by rate descending.
 */
export const invoiceTotalsSchema = z.object({
    subtotal: z.number(),
    taxByRate: z.array(invoiceTaxByRateSchema),
    amountTotal: z.number(),
    /** AR `amount_received` / AP `amount_paid`. */
    amountPaid: z.number(),
    /** AR `amount_outstanding` / AP `amount_remaining`. */
    amountRemaining: z.number(),
});
/**
 * A real payment against the invoice — what survives
 * `filterPaymentTransactions` (no posting, no void, no force-edit-superseded
 * journal), sorted oldest first so the template's "Payment #N" numbering is
 * chronological.
 */
export const invoicePaymentSchema = z.object({
    id: z.string(),
    transactionNumber: z.string(),
    /**
     * `ledger_transactions.transaction_date` — a plain `date` (`YYYY-MM-DD`), a
     * calendar day carrying no instant. The template's formatter routes
     * date-only values to `safeFormatDate` (see `isDateOnly` in
     * `packages/documents/.../formatters.ts`), so the payment history reads the
     * same day on screen and in the server-printed PDF without consulting
     * `meta.orgTimezone`.
     */
    transactionDate: z.string(),
    amount: z.number(),
    reference: z.string().nullable(),
});
export const invoiceDocumentSchema = z.object({
    meta: documentMetaSchema,
    org: orgIdentityBlockSchema,
    /**
     * Print the org's IBAN/BIC. True for AR only: a customer needs to know where
     * to pay, a vendor's own bill must not carry our bank details (the template
     * gates its banking block on this flag alone).
     */
    showBanking: z.boolean(),
    invoiceType: invoiceTypeSchema,
    /**
     * Null for an AR DRAFT (20261101120000): a draft has no number — Issue
     * assigns the next one in the series. The template then titles the
     * document "Draft invoice" and prints no number line.
     */
    invoiceNumber: z.string().nullable(),
    /**
     * `accounts_receivable.invoice_date` / `accounts_payable.invoice_date` — a
     * `timestamptz` on both tables, so this is an instant, not a calendar day.
     * The template resolves it to a day in the org's timezone
     * (`meta.orgTimezone`) at render time; resolving it against the rendering
     * process's timezone instead would print one date in a US browser and the
     * previous day in the UTC server PDF, on the field EU VAT law judges the
     * invoice by.
     */
    invoiceDate: z.string(),
    /**
     * AR only: the supply date — the day the goods or services were delivered
     * (`accounts_receivable.metadata.supply_date`, 20261101120000), a calendar
     * day `YYYY-MM-DD`. Null when it is the invoice date (the template then
     * prints no supply-date line) and always null on AP.
     */
    supplyDate: z.string().nullable(),
    /** `due_date` — a true DATE column on both tables, so `YYYY-MM-DD`. */
    dueDate: z.string().nullable(),
    /**
     * AR only: the day a CANCELLED invoice was cancelled — the reversal
     * journal's `transaction_date` (`accounts_receivable.void_transaction_id`,
     * written by `cancel_ar_invoice`), a calendar day `YYYY-MM-DD`. The template
     * prints "Cancelled on <date>" in place of the due date. Null on every other
     * invoice, on AP, and on a cancelled invoice with no reversal journal.
     */
    cancelledOn: z.string().nullable(),
    /** AR `status` / AP `payment_status`. */
    status: z.string().nullable(),
    /** AP only (`approval_status`); null on AR. */
    approvalStatus: z.string().nullable(),
    /** The invoice memo — rendered under "Note to customer". */
    description: z.string().nullable(),
    paymentTerms: z.string().nullable(),
    /**
     * Both of these are form fields bound to columns that DO NOT EXIST on
     * `accounts_receivable` / `accounts_payable` and were never persisted (see
     * migration 20260727110000's header). They are read defensively so the
     * document picks them up for free if the columns ever land; until then they
     * are always `false` / `null`.
     */
    selfBilling: z.boolean(),
    additionalVatNotes: z.string().nullable(),
    /**
     * Statutory statements the invoice as a WHOLE must carry, as codes the
     * template names (`invoice.taxStatements.<code>`) — `reverse_charge`, and
     * `non_reclaimable` (an AP bill with a "VAT not reclaimable" line: the
     * total includes VAT that was not reclaimed). Derived from the line items here, never stored: the AR
     * form used to append this sentence to `description` on every save, which
     * meant reading a derived value back as user input and stacking one more
     * copy per save. Per-line statements (an exemption's article reference) are
     * NOT repeated here — the template already prints those against their own
     * line.
     */
    taxStatements: z.array(z.string()),
    /**
     * The currency every amount on this document is expressed in: the
     * currency of the invoice's control account (`metadata.ar_account_id` /
     * `metadata.ap_account_id`, single-currency ledger gate), else the org's
     * base currency when that account has none. Never `original_currency`
     * (legacy, unwritten).
     */
    currency: z.string(),
    counterparty: invoiceCounterpartySchema,
    /** Sorted by `sort_order` ascending. */
    lineItems: z.array(invoiceLineItemSchema),
    totals: invoiceTotalsSchema,
    payments: z.array(invoicePaymentSchema),
});
//# sourceMappingURL=model.js.map